import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, X, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId] = useState(() => {
    // Generate unique session ID on component mount
    return 'session_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  });
  const [showChoiceButtons, setShowChoiceButtons] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m Brendan. I\'m the front-of-house bot here, and my only job is to make sure our team gets exactly what they need to take this task off your plate.\n\nI know you\'re busy, so how would you like to do this?',
      showButtons: true
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleChoiceClick = async (choice) => {
    setShowChoiceButtons(false);
    
    if (choice === 'chat') {
      const userMessage = { role: 'user', content: 'Let\'s Chat' };
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: 'USER_CHOSE_CHAT',
            sessionId: sessionId,
            conversationHistory: messages
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to get response');
        }

        const data = await response.json();
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response 
        }]);
        setIsTyping(false);
        
      } catch (error) {
        console.error('Chat error:', error);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Great choice! In just a few words, what is the main thing you want us to handle for you? (Don\'t worry about the tech details yet—just tell me the \'what\' and the \'why\')' 
        }]);
        setIsTyping(false);
      }
    } else if (choice === 'form') {
      const userMessage = { role: 'user', content: 'Give me the Form' };
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);

      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Perfect! You can access our simple one-page form in two ways:\n\n1. Click the "Get Started" button at the top of the page\n2. Scroll down to the "Let\'s Work Together" section\n\nBoth will take you to the same form with all the questions laid out. Take your time filling it in!' 
        }]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Real API call to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: input,
          sessionId: sessionId,
          conversationHistory: messages
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response 
      }]);
      setIsTyping(false);
      
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again or contact us at contact@taitconsultancy.com.' 
      }]);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          {/* Pulsing indicator */}
          <div 
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
              opacity: 0.4
            }}
          />
          <button
            onClick={() => setIsOpen(true)}
            className="relative shadow-2xl hover:shadow-3xl hover:scale-105 transition-transform"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
              border: '3px solid #000000',
              overflow: 'hidden'
            }}
            aria-label="Chat with Brendan"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_ai-advisor-tait/artifacts/qwgftd4m_lv_7588133859170979089_20260218194808.mp4" type="video/mp4" />
            </video>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 bg-card shadow-2xl"
          style={{
            width: '400px',
            maxWidth: 'calc(100vw - 2rem)',
            height: '600px',
            maxHeight: 'calc(100vh - 2rem)',
            borderRadius: '1rem',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--border-primary)'
          }}
        >
          {/* Chat Header */}
          <div 
            className="flex items-center justify-between p-4"
            style={{
              background: 'linear-gradient(135deg, #000000 0%, #C41E3A 100%)',
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem'
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #FFFFFF',
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  overflow: 'hidden'
                }}
              >
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_ai-advisor-tait/artifacts/qwgftd4m_lv_7588133859170979089_20260218194808.mp4" type="video/mp4" />
                </video>
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-2px',
                    right: '-2px',
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    background: '#2E7D32',
                    border: '2px solid #FFFFFF'
                  }}
                />
              </div>
              <div>
                <h3 className="font-medium" style={{ color: '#FFFFFF', fontSize: '1.1rem', marginBottom: '2px' }}>Brendan</h3>
                <p className="text-xs" style={{ color: '#D4AF37', fontWeight: '500' }}>Your AI Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 transition-all"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Close chat"
            >
              <X size={20} color="#FFFFFF" />
            </button>
          </div>

          {/* Messages Area */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-4"
            style={{ background: 'var(--bg-page)' }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className="flex gap-3"
                style={{
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                {message.role === 'assistant' && (
                  <div 
                    className="flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #000000',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    >
                      <source src="https://customer-assets.emergentagent.com/job_ai-advisor-tait/artifacts/qwgftd4m_lv_7588133859170979089_20260218194808.mp4" type="video/mp4" />
                    </video>
                  </div>
                )}
                <div
                  className="max-w-[75%] p-3 rounded-lg"
                  style={{
                    background: message.role === 'user' 
                      ? 'var(--text-primary)' 
                      : 'var(--bg-card)',
                    color: message.role === 'user' ? 'white' : 'var(--text-primary)',
                    border: message.role === 'assistant' ? '1px solid var(--border-light)' : 'none',
                    fontSize: '0.875rem',
                    lineHeight: '1.5'
                  }}
                >
                  {message.content}
                </div>
                {message.showButtons && showChoiceButtons && message.role === 'assistant' && (
                  <div className="flex flex-col gap-2 mt-3">
                    <button
                      onClick={() => handleChoiceClick('chat')}
                      className="btn-primary hover-scale"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                        border: '2px solid #D4AF37',
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        letterSpacing: 'normal'
                      }}
                    >
                      💬 Let's Chat (Easiest)
                      <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem' }}>
                        I'll ask a few simple questions
                      </div>
                    </button>
                    <button
                      onClick={() => handleChoiceClick('form')}
                      className="btn-secondary hover-scale"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        background: 'var(--glass-bg)',
                        border: '2px solid #D4AF37',
                        color: 'var(--text-primary)',
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        letterSpacing: 'normal'
                      }}
                    >
                      📝 Give me the Form (Quickest)
                      <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                        See all questions at once
                      </div>
                    </button>
                  </div>
                )}
                {message.role === 'user' && (
                  <div 
                    className="flex-shrink-0"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <User size={18} color="white" />
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-3">
                <div 
                  className="flex-shrink-0"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #000000',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  >
                    <source src="https://customer-assets.emergentagent.com/job_ai-advisor-tait/artifacts/qwgftd4m_lv_7588133859170979089_20260218194808.mp4" type="video/mp4" />
                  </video>
                </div>
                <div
                  className="p-3 rounded-lg"
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="flex gap-1">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div 
            className="p-4 border-t"
            style={{
              background: 'var(--bg-card)',
              borderColor: 'var(--border-light)',
              borderBottomLeftRadius: '1rem',
              borderBottomRightRadius: '1rem'
            }}
          >
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
                style={{
                  border: '1px solid var(--border-input)',
                  borderRadius: '2rem',
                  padding: '0.75rem 1rem'
                }}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim()}
                className="btn-primary"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Send size={20} />
              </Button>
            </div>
            <p style={{ 
              fontSize: '0.6rem', 
              color: 'var(--text-muted)', 
              textAlign: 'center', 
              marginTop: '0.5rem',
              opacity: 0.7
            }}>
              By sending a message, you agree to our <span style={{ color: '#D4AF37', cursor: 'pointer' }} onClick={() => document.querySelector('footer').scrollIntoView({ behavior: 'smooth' })}>AI Disclaimer</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;